import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { InputField, ErrorMessage } from 'anf-core-react';

export default function ExpiryField({
  expiryDate,
  label,
  errorMessage,
  index,
  onChange,
  hasValidationError,
}) {
  const [isInvalid, setIsInvalid] = useState(false);

  useEffect(() => {
    setIsInvalid(!!hasValidationError);
  }, [hasValidationError]);

  const handleChange = (event) => {
    const input = event.target.value;
    const previousValue = expiryDate;

    // Remove all non-digits first
    let cleanInput = input.replace(/[^\d]/g, '');

    // Limit to 4 digits max
    if (cleanInput.length > 4) {
      cleanInput = cleanInput.substring(0, 4);
    }

    // Add slash after 2 digits if we have more than 2 digits
    if (cleanInput.length >= 3) {
      cleanInput = `${cleanInput.substring(0, 2)}/${cleanInput.substring(2)}`;
    }

    // Only call onChange if the value actually changed
    if (cleanInput !== previousValue) {
      onChange(cleanInput);
    }
  };

  const handleKeyDown = (event) => {
    // Allow backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1
        // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        || (event.keyCode === 65 && event.ctrlKey === true)
        || (event.keyCode === 67 && event.ctrlKey === true)
        || (event.keyCode === 86 && event.ctrlKey === true)
        || (event.keyCode === 88 && event.ctrlKey === true)) {
      return;
    }

    // Ensure that it is a number and stop the keypress
    if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && (event.keyCode < 96 || event.keyCode > 105)) {
      event.preventDefault();
    }
  };

  const validateChange = (event) => {
    const input = event.target.value;
    // Basic validation: should be MM/YY format
    const isValidFormat = /^\d{2}\/\d{2}$/.test(input);

    if (input.length > 0 && !isValidFormat) {
      setIsInvalid(true);
    } else if (isValidFormat) {
      // Additional validation for month (01-12)
      const month = parseInt(input.substring(0, 2), 10);
      setIsInvalid(month < 1 || month > 12);
    } else {
      setIsInvalid(false);
    }
  };

  return (
    <InputField
      label={label}
      name={`creditCard[${index}].expiryDate`}
      id={`expiry-field-${index}`}
      value={expiryDate}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={validateChange}
      type="tel"
      maxLength={5}
      placeholder="MM/YY"
      isInvalid={isInvalid}
      autoComplete="cc-exp"
      classList="expiry-field"
      inputProps={{
        'data-validationrule': 'expiry',
        'data-testid': `expiry-field-${index}`,
      }}
    >
      {errorMessage && (
        <ErrorMessage id="expiry-error-message">
            {errorMessage}
        </ErrorMessage>
      )}
    </InputField>
  );
}

ExpiryField.propTypes = {
  label: PropTypes.string,
  errorMessage: PropTypes.string,
  expiryDate: PropTypes.string,
  index: PropTypes.number,
  onChange: PropTypes.func,
  hasValidationError: PropTypes.bool,
};

ExpiryField.defaultProps = {
  label: '',
  errorMessage: '',
  expiryDate: '',
  index: 0,
  onChange: () => {},
  hasValidationError: false,
};
